import flow from 'lodash/flow';
import get from 'lodash/get';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { bindActionCreators } from 'redux';

import { createAccount } from '../../actions/subscription';
import { logSubscribeActivity } from '../../actions/logging';
import { trackOptInConfirmationPage } from '../../actions/pageTracking';

import billingFrequency from '../../utils/subscription/billingPeriod';
import { addPaymentErrors } from '../../utils/form/paymentValidation';
import gReportConversion from '../../../client/utils/gReportConversion';
import { fbTrack, fbPixelStandardEvents } from '../../utils/fbTrack';
import generateOriginLabelFromQuery from '../../utils/generateOriginLabelFromQuery';
import {
  SUBSCRIBE_PAYMENT_FORM_DEFAULT_ERROR,
  SUBSCRIBE_PAYMENT_FORM_SUCCESS_DIALOG,
} from '../../constants/localizations/subscription';
import subscribe from '../../constants/analytics/categoryActionLabel/subscribe';
import {
  SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_HEADER,
  SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_SUB_HEADER,
} from '../../constants/localizations/payment';
import PaymentForm from './PaymentForm';

const FORM_ID = 'subscribe';

/**
 * A payment form that is specific to the Subscribe component. The SubscribePaymentForm extends the
 * base level PaymentForm component and overrides the onSubmit handler.
 */
export class SubscribePaymentForm extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    breakpoint: PropTypes.number.isRequired,
    products: PropTypes.array.isRequired,
    couponData: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
  }

  /**
   * A function that is responsible for taking a product and a token, generated by either the
   * CreditCard or PaypalPayment components, and submitting relevant info to the gemini server via
   * the actions.createAccount action creator, as well as logging subscribe activity, reporting
   * conversions to Google, and tracking Facebook pixel events
   * @param product {object}
   * @param token {string}
   * @returns {Promise}
   */
  async onSubmit(product, token) {
    const { actions, location, couponData } = this.props;
    // todo: test that query is passed through
    const querySource =
      generateOriginLabelFromQuery(location) || subscribe.labels.payment;
    const couponCode = get(couponData, 'code');

    fbTrack(fbPixelStandardEvents.InitiateCheckout, {
      value: (product.price / 100).toFixed(2),
      currency: product.currencyCode,
    });

    await actions.createAccount(
      product.currencyCode,
      token,
      querySource,
      product.sku,
      couponCode,
    );

    actions.logSubscribeActivity(
      subscribe.actions.show,
      subscribe.labels.confirmation,
    );

    let buyAction = subscribe.actions.buyMonthly;
    if (product.billingPeriod === billingFrequency.yearly) {
      buyAction = subscribe.actions.buyYearly;
    } else if (product.billingPeriod === billingFrequency.quarterly) {
      buyAction = subscribe.actions.buyQuarterly;
    }

    actions.logSubscribeActivity(
      buyAction, // driven by sku
      querySource, // driven by upsell
    );

    gReportConversion();

    actions.trackOptInConfirmationPage(product.price);

    fbTrack(fbPixelStandardEvents.StartTrial, {
      value: (product.price / 100).toFixed(2),
      currency: product.currencyCode,
    });
  }

  render() {
    return (
      <PaymentForm
        {...this.props}
        formId={FORM_ID}
        onSubmit={this.onSubmit}
        defaultErrorMessage={SUBSCRIBE_PAYMENT_FORM_DEFAULT_ERROR}
        successDialogContent={SUBSCRIBE_PAYMENT_FORM_SUCCESS_DIALOG}
        header={SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_HEADER}
        subheader={SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_SUB_HEADER}
        redirectOnSuccess
      />
    );
  }
}

export function mapStateToProps(state) {
  return {
    form: state.form.subscribe,
  };
}

export function mapDispatchToProps(dispatch) {
  const actions = {
    createAccount,
    logSubscribeActivity,
    trackOptInConfirmationPage,
  };

  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: FORM_ID,
    validate: addPaymentErrors,
  }),
)(SubscribePaymentForm);
